import { useState, useEffect } from 'react';
import kit from './kit.module.css';
import styles from './GracefulFailureDemoVoice.module.css';

type Example = 'good' | 'bad';
type State = 'idle' | Example;

interface Line {
  role: 'user' | 'agent' | 'silence';
  text: string;
  delay: number;
}

const GOOD_LINES: Line[] = [
  { role: 'user', text: 'Send the CV to the employer.', delay: 0 },
  { role: 'agent', text: "I wasn't able to send it - I don't have an employer email address for this case.", delay: 800 },
  { role: 'agent', text: 'You can say: add email address, skip sending, or cancel.', delay: 1600 },
  { role: 'user', text: 'Add email address.', delay: 2400 },
  { role: 'agent', text: "Got it. What's the email address?", delay: 3200 },
];

const BAD_LINES: Line[] = [
  { role: 'user', text: 'Send the CV to the employer.', delay: 0 },
  { role: 'agent', text: 'An error occurred.', delay: 800 },
  { role: 'silence', text: '', delay: 1600 },
  { role: 'user', text: 'Hello? Did it send?', delay: 6000 },
  { role: 'agent', text: 'An error occurred.', delay: 6800 },
];

function Silence() {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setCount((c) => (c < 4 ? c + 1 : c)), 1000);
    return () => clearInterval(t);
  }, []);

  return <div className={`${kit.cue} ${styles.fadeIn}`}>({count}s of silence...)</div>;
}

export function GracefulFailureDemoVoice() {
  const [state, setState] = useState<State>('idle');
  const [visibleCount, setVisibleCount] = useState(0);

  const lines = state === 'good' ? GOOD_LINES : state === 'bad' ? BAD_LINES : [];
  const isGood = state === 'good';
  const allVisible = visibleCount >= lines.length;

  useEffect(() => {
    setVisibleCount(0);
    if (state === 'idle') return;
    const source = state === 'good' ? GOOD_LINES : BAD_LINES;
    const timers = source.map((line, i) => setTimeout(() => setVisibleCount(i + 1), line.delay));
    return () => timers.forEach(clearTimeout);
  }, [state]);

  if (state === 'idle') {
    return (
      <div className={kit.root}>
        <div className={kit.orb} aria-hidden="true" />
        <div className={styles.center}>
          <p className={kit.feedback}>Hear how a voice agent handles a failure.</p>
          <div className={`${kit.row} ${styles.centerRow}`}>
            <button type="button" className={`${kit.btn} ${kit.btnPrimary}`} onClick={() => setState('good')}>
              Good example
            </button>
            <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={() => setState('bad')}>
              Bad example
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={kit.root}>
      <div className={`${kit.row} ${styles.centerRow}`}>
        <span className={`${kit.chip} ${isGood ? kit.chipGood : kit.chipBad}`}>
          {isGood ? 'GOOD EXAMPLE' : 'BAD EXAMPLE'}
        </span>
      </div>
      <div className={`${kit.orb} ${!allVisible ? kit.orbActive : ''}`} aria-hidden="true" />

      <div className={kit.transcript} aria-live="polite">
        {lines.slice(0, visibleCount).map((line, i) =>
          line.role === 'silence' ? (
            <Silence key={i} />
          ) : (
            <div
              key={i}
              className={`${kit.voiceLine} ${line.role === 'agent' ? kit.voiceAgent : ''} ${styles.fadeIn}`}
            >
              <span className={kit.voiceWho}>{line.role === 'agent' ? 'AGENT' : 'YOU'}</span>
              <p className={line.role === 'agent' && !isGood ? styles.badLine : undefined}>{line.text}</p>
            </div>
          )
        )}
      </div>

      {allVisible && (
        <div className={styles.center}>
          <p className={kit.feedback}>
            {isGood
              ? 'Says what went wrong, offers spoken options, keeps the task moving.'
              : 'No explanation. No next step. The user is stuck.'}
          </p>
          <button
            type="button"
            className={`${kit.btn} ${kit.btnEdit}`}
            onClick={() => setState(isGood ? 'bad' : 'good')}
          >
            Hear the {isGood ? 'bad' : 'good'} example
          </button>
        </div>
      )}
    </div>
  );
}
